import React, { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { SessionProvider, useSession } from './context/Session';
import { CelebrationProvider, ConfirmProvider, Spinner, ToastProvider, useCelebrate } from './components/ui';
import BadgeMedal from './components/BadgeMedal';
import { BADGE_CATALOG, checkAndAwardBadges } from './lib/badges';
import { requestXpSync, titleAt } from './lib/xp';
import { prizeName } from './components/xp';
import { GlowBackground } from './layout/AppShell';
import AppShell from './layout/AppShell';
import Landing from './pages/Landing';
import Home from './pages/Home';
import ProgramPage from './pages/Program';
import Nutrition from './pages/Nutrition';
import NutritionRecords from './pages/NutritionRecords';
import Progress, { ProgressRecords } from './pages/Progress';
import AiTrainer from './pages/AiTrainer';
import Trainees, { TraineeProgramPage } from './pages/Trainees';
import Profile from './pages/Profile';
import Settings from './pages/Settings';
import Badges from './pages/Badges';
import WeeklySummary from './pages/WeeklySummary';
import Admin from './pages/Admin';
import { useT } from './i18n';
import './styles/app.css';

function Splash() {
  return (
    <div className="gm">
      <GlowBackground />
      <div className="gm-fullscreen-center" style={{ position: 'relative', zIndex: 1 }}>
        <img src="/gymgm_logo.jpg" alt="GymGM" style={{ width: 64, height: 64, borderRadius: 14 }} />
        <Spinner />
      </div>
    </div>
  );
}

/**
 * Celebrates newly earned badges anywhere in the app (the app's BadgeCelebrationListener),
 * queued after any meal/streak celebration already showing.
 */
function BadgeCelebrations() {
  const t = useT();
  const celebrate = useCelebrate();
  const { user } = useSession();
  const uid = user.isTrainer ? null : user.id;
  useEffect(() => {
    const fn = (e) => (e.detail || []).forEach(({ id, count }) => {
      const badge = BADGE_CATALOG.find((b) => b.id === id);
      if (!badge) return;
      celebrate({
        medal: <BadgeMedal badge={badge} earned size={96} count={count} />,
        color: badge.color,
        message: count > 1 ? t('badgeEarnedAgainMessage', t.badgeTitle(id), count) : t('badgeNewlyEarnedMessage', t.badgeTitle(id)),
      });
    });
    window.addEventListener('gm:badges-earned', fn);
    return () => window.removeEventListener('gm:badges-earned', fn);
  }, [t, celebrate]);
  // Awards normally happen on a write, so badges whose condition history already meets
  // (new ones from an update) would stay locked until the next workout/meal. Once per
  // page load (the check reads every collection), with or without a program.
  useEffect(() => {
    if (!uid || badgeCheckedUids.has(uid)) return;
    badgeCheckedUids.add(uid);
    checkAndAwardBadges(uid).catch(() => {});
  }, [uid]);
  return null;
}

const XP_SOURCE_LABELS = {
  meals: 'xpSourceMeals',
  protein: 'xpSourceProtein',
  water: 'xpSourceWater',
  workout: 'xpSourceWorkout',
  week: 'xpSourceWeek',
};
const XP_SOURCE_COLORS = { meals: '#00C896', protein: '#E5484D', water: '#2E9BFF', workout: '#FF6D00', week: '#FF6D00' };

/**
 * Plays the XP the server just paid (the app's XpCelebrationListener): a "+10 XP"
 * pill per source, queued after the meal/workout celebration that caused it,
 * then a full celebration per level reached (with its chest prize) and per title.
 * Also asks for a sync on load and when the tab comes back, so logs made in the
 * mobile app or offline still pay and celebrate here.
 */
function XpCelebrations() {
  const t = useT();
  const celebrate = useCelebrate();
  const { user } = useSession();
  const trainee = !user.isTrainer;
  useEffect(() => {
    if (!trainee) return undefined;
    const fn = (e) => {
      const { gains = [], levelUps = [] } = e.detail || {};
      gains.forEach(({ source, xp }) => {
        if (!XP_SOURCE_LABELS[source] || !(xp > 0)) return;
        celebrate({
          compact: true,
          emoji: '⚡',
          title: t('xpGained', xp),
          count: xp,
          countTitle: (n) => t('xpGained', n),
          message: t(XP_SOURCE_LABELS[source]),
          color: XP_SOURCE_COLORS[source],
        });
      });
      levelUps.forEach(({ level, prize }) => {
        celebrate({
          emoji: '🎁',
          color: '#FDD835',
          message: `${t('xpLevelUpMessage', level)}\n${prize ? t('xpChestPrize', prizeName(t, prize)) : t('xpChestFull')}`,
        });
        const title = titleAt(level);
        if (title) celebrate({ emoji: '👑', color: '#FF8C00', message: t('xpTitleUnlocked', t.xpTitle(title.id)) });
      });
    };
    window.addEventListener('gm:xp-earned', fn);
    return () => window.removeEventListener('gm:xp-earned', fn);
  }, [t, celebrate, trainee]);
  useEffect(() => {
    if (!trainee) return undefined;
    requestXpSync();
    const onVisible = () => document.visibilityState === 'visible' && requestXpSync();
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [trainee, user.id]);
  return null;
}

// Users already badge-checked this page load (see BadgeCelebrations).
const badgeCheckedUids = new Set();

function RoleRoute({ trainer, children }) {
  const { user } = useSession();
  if (trainer !== undefined && user.isTrainer !== trainer) return <Navigate to="/home" replace />;
  return children;
}

function AdminRoute({ children }) {
  const { isAdmin } = useSession();
  if (isAdmin === null) return <Spinner />;
  if (!isAdmin) return <Navigate to="/home" replace />;
  return children;
}

function Gate() {
  const { authUser, user, loading } = useSession();
  const location = useLocation();
  if (loading) return <Splash />;
  if (!authUser) return <Landing />;
  if (!user) return <Splash />;
  if (location.pathname === '/' || location.pathname === '') return <Navigate to="/home" replace />;
  return (
    <>
      <BadgeCelebrations />
      <XpCelebrations />
      <Routes>
        <Route element={<AppShell />}>
          <Route path="/home" element={<Home />} />
          <Route path="/program" element={<RoleRoute trainer={false}><ProgramPage /></RoleRoute>} />
          <Route path="/nutrition" element={<RoleRoute trainer={false}><Nutrition /></RoleRoute>} />
          <Route path="/nutrition/records" element={<RoleRoute trainer={false}><NutritionRecords /></RoleRoute>} />
          <Route path="/progress" element={<RoleRoute trainer={false}><Progress /></RoleRoute>} />
          <Route path="/progress/records" element={<RoleRoute trainer={false}><ProgressRecords /></RoleRoute>} />
          <Route path="/ai-trainer" element={<RoleRoute trainer={false}><AiTrainer /></RoleRoute>} />
          <Route path="/badges" element={<RoleRoute trainer={false}><Badges /></RoleRoute>} />
          <Route path="/weekly-summary" element={<RoleRoute trainer={false}><WeeklySummary /></RoleRoute>} />
          <Route path="/trainees" element={<RoleRoute trainer><Trainees /></RoleRoute>} />
          <Route path="/trainees/:traineeId/program" element={<RoleRoute trainer><TraineeProgramPage /></RoleRoute>} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/admin" element={<AdminRoute><Admin /></AdminRoute>} />
          <Route path="*" element={<Navigate to="/home" replace />} />
        </Route>
      </Routes>
    </>
  );
}

export default function WebApp() {
  return (
    <SessionProvider>
      <ToastProvider>
        <ConfirmProvider>
          <CelebrationProvider>
            <Gate />
          </CelebrationProvider>
        </ConfirmProvider>
      </ToastProvider>
    </SessionProvider>
  );
}
